package com.sweetlime.dungeonamigo.presentation.features.character

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.presentation.ui.components.*
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.classColorFor
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import com.sweetlime.dungeonamigo.presentation.ui.theme.silkFont
import org.koin.compose.koinInject

object CharacterListScreen : Screen {
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: CharacterViewModel = koinInject()
        val state by vm.characterListState.collectAsState()
        var showUpgradeDialog by remember { mutableStateOf(false) }

        LaunchedEffect(state) {
            if (state is CharacterListState.ShowUpgradePrompt) showUpgradeDialog = true
        }

        Column(
            modifier = Modifier.fillMaxSize().background(BrandColors.Vault),
        ) {
            when (val s = state) {
                is CharacterListState.Loading -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = BrandColors.Parchment)
                    }
                }

                is CharacterListState.Error -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text("⚠️", fontSize = 40.sp)
                            Spacer(Modifier.height(8.dp))
                            Text(s.message, color = BrandColors.IvoryDim, fontSize = 13.sp)
                        }
                    }
                }

                is CharacterListState.Ready, is CharacterListState.ShowUpgradePrompt -> {
                    val chars = (s as? CharacterListState.Ready)?.characters ?: emptyList()
                    val isLocked = chars.size >= 3

                    if (chars.isEmpty()) {
                        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            EmptyStateView(
                                illustration = { CharactersEmptyIllustration() },
                                headline = "Your legend begins here",
                                subtext = "Create your first character to start your adventure",
                                ctaText = "Create Character",
                                onCtaClick = {
                                    vm.resetWizard()
                                    navigator.push(CharacterCreationScreen)
                                },
                                modifier = Modifier.padding(32.dp),
                            )
                        }
                    } else {
                        Box(Modifier.fillMaxSize().weight(1f)) {
                            LazyColumn(
                                modifier = Modifier.fillMaxSize(),
                                contentPadding = PaddingValues(
                                    top = 4.dp,
                                    start = 18.dp,
                                    end = 18.dp,
                                    bottom = 100.dp,
                                ),
                                verticalArrangement = Arrangement.spacedBy(14.dp),
                            ) {
                                item {
                                    SectionTitle(
                                        title = "Your Party",
                                        rightLabel = "${chars.size} HEROES",
                                    )
                                }
                                items(chars) { character ->
                                    CharacterCard(
                                        character = character,
                                        onClick = { navigator.push(CharacterSheetScreen(character.id)) },
                                    )
                                }
                                item { EmptyHintCard() }
                            }

                            // FAB with chunky EmberDim offset shadow
                            PixelFab(
                                isLocked = isLocked,
                                modifier = Modifier.align(Alignment.BottomEnd).padding(24.dp),
                                onClick = {
                                    if (isLocked) showUpgradeDialog = true
                                    else {
                                        vm.resetWizard()
                                        navigator.push(CharacterCreationScreen)
                                    }
                                },
                            )
                        }
                    }
                }
            }
        }

        if (showUpgradeDialog) {
            AlertDialog(
                onDismissRequest = { showUpgradeDialog = false },
                containerColor = BrandColors.Stone,
                title = { Text("Character Limit Reached", color = BrandColors.Ivory) },
                text = {
                    Text(
                        "Free tier allows up to 3 characters. Upgrade to Plus for unlimited characters.",
                        color = BrandColors.IvoryDim,
                    )
                },
                confirmButton = {
                    TextButton(onClick = { showUpgradeDialog = false }) {
                        Text("OK", color = BrandColors.Parchment)
                    }
                },
            )
        }
    }
}

@Composable
private fun CharacterCard(character: Character, onClick: () -> Unit) {
    val pf = pixelFont()
    val sf = silkFont()
    val hpFraction = if (character.maxHp > 0) character.currentHp.toFloat() / character.maxHp else 0f
    val hpColor = when {
        hpFraction > 0.5f  -> BrandColors.Ember
        hpFraction > 0.25f -> BrandColors.EmberAmber
        else               -> BrandColors.EmberDim
    }
    val classColor = classColorFor(character.characterClass)

    Surface(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        shape = RoundedCornerShape(14.dp),
        color = BrandColors.Stone,
        border = BorderStroke(1.dp, BrandColors.Slate.copy(alpha = 0.4f)),
        shadowElevation = 4.dp,
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(14.dp),
            verticalAlignment = Alignment.Top,
        ) {
            AvatarTile(character = character, classColor = classColor, size = 60.dp)

            Column(Modifier.weight(1f)) {
                // Name + HP readout
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Bottom,
                ) {
                    Text(
                        text = character.name.ifBlank { "Unnamed Hero" },
                        color = BrandColors.Ivory,
                        fontFamily = pf,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 21.sp,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false).padding(end = 8.dp),
                    )
                    Row(verticalAlignment = Alignment.Bottom) {
                        Text("${character.currentHp}", color = hpColor, fontFamily = pf, fontSize = 19.sp, fontWeight = FontWeight.SemiBold)
                        Text("/${character.maxHp}", color = BrandColors.IvoryDim, fontFamily = pf, fontSize = 14.sp)
                        Spacer(Modifier.width(3.dp))
                        Text("HP", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 8.sp, letterSpacing = 1.sp, modifier = Modifier.padding(bottom = 2.dp))
                    }
                }

                // Subtitle: race class · Lv n
                Spacer(Modifier.height(4.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    val raceClass = buildString {
                        if (character.race.isNotBlank()) append(character.race)
                        if (character.characterClass.isNotBlank()) {
                            if (isNotEmpty()) append(" ")
                            append(character.characterClass)
                        }
                    }
                    if (raceClass.isNotBlank()) {
                        Text(raceClass, color = BrandColors.IvoryDim, fontSize = 13.5.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    }
                    if (character.level > 0) {
                        Spacer(Modifier.width(6.dp))
                        Box(Modifier.size(4.dp).background(BrandColors.Slate, CircleShape))
                        Spacer(Modifier.width(6.dp))
                        Text("Lv ${character.level}", color = BrandColors.Parchment, fontFamily = pf, fontSize = 14.sp)
                    }
                }

                // Segmented HP bar
                Spacer(Modifier.height(10.dp))
                PixelHpBar(cur = character.currentHp, max = character.maxHp)
            }
        }
    }
}


@Composable
private fun EmptyHintCard() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .drawBehind {
                val strokeWidth = 1.dp.toPx()
                drawRoundRect(
                    color = BrandColors.Slate.copy(alpha = 0.47f),
                    cornerRadius = CornerRadius(12.dp.toPx()),
                    style = Stroke(
                        width = strokeWidth,
                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(8f, 6f), 0f),
                    ),
                )
            }
            .padding(16.dp),
    ) {
        Text(
            text = "Tap a hero to manage HP, conditions & roll checks. Hit + to forge a new one.",
            color = BrandColors.IvoryDim,
            fontSize = 13.sp,
        )
    }
}

@Composable
private fun PixelFab(isLocked: Boolean, modifier: Modifier = Modifier, onClick: () -> Unit) {
    val pf = pixelFont()
    Box(modifier = modifier) {
        if (!isLocked) {
            // Chunky EmberDim offset shadow layer
            Box(
                Modifier
                    .size(58.dp)
                    .offset(y = 5.dp)
                    .clip(RoundedCornerShape(17.dp))
                    .background(BrandColors.EmberDim),
            )
        }
        FloatingActionButton(
            onClick = onClick,
            modifier = Modifier.size(58.dp),
            shape = RoundedCornerShape(17.dp),
            containerColor = if (isLocked) BrandColors.Stone else BrandColors.Ember,
            contentColor = BrandColors.Ivory,
            elevation = FloatingActionButtonDefaults.elevation(
                defaultElevation = 0.dp,
                pressedElevation = 0.dp,
            ),
        ) {
            if (isLocked) {
                Icon(Icons.Filled.Lock, contentDescription = "Character limit reached")
            } else {
                Text("+", fontFamily = pf, fontSize = 32.sp, fontWeight = FontWeight.Bold, color = BrandColors.Ivory)
            }
        }
    }
}
